<template>
  <div class="my-4 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
    <!-- Section header — inline title + selector -->
    <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-gray-50">
      <div>
        <h3 class="text-lg font-semibold text-gray-800">Course Registrations</h3>
        <p class="text-xs text-gray-500 mt-0.5">Showing all terms — pick an academic calendar to narrow down.</p>
      </div>
      <div class="w-full sm:w-80">
        <!-- No auto-selected calendar: the default view is the student's full
             registration history, not just the current term. -->
        <AcademicCalendarSelect v-model="selectedCalendarId" :autoSelectActive="false"
          placeholder="All Academic Calendars" className="w-full p-inputtext-sm" data-testid="course-reg-calendar-select" />
      </div>
    </div>

    <!-- Empty state when no registrations match -->
    <div
      v-if="displayedRegistrations.length === 0"
      class="px-5 py-8 text-center text-sm text-gray-400"
      data-testid="course-reg-empty"
    >
      {{ selectedCalendarId ? 'No course registrations for the selected calendar.' : 'No course registrations yet.' }}
    </div>

    <!-- REGISTRATION SECTIONS -->
    <div
      v-for="registration in displayedRegistrations"
      :key="registration.id"
      class="border-b border-gray-200 last:border-b-0"
      data-testid="course-reg-section"
    >
      <!-- Card header -->
      <div class="px-5 pt-5 pb-4">
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <h3 class="text-lg font-semibold text-gray-900 truncate">
                {{ registration.programme?.program_name }}
              </h3>
              <Tag severity="info" :value="registration.academic_session" />
            </div>
            <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
              <span class="inline-flex items-center gap-1">
                <i class="pi pi-graduation-cap text-xs text-gray-400"></i>
                <span class="font-medium text-gray-700">{{ registration.programme?.degree || '—' }}</span>
              </span>
              <span class="inline-flex items-center gap-1">
                <i class="pi pi-bookmark text-xs text-gray-400"></i>
                Level {{ registration.registration_level }}
              </span>
              <span class="inline-flex items-center gap-1">
                <i class="pi pi-calendar text-xs text-gray-400"></i>
                {{ semesterLabel(registration.semester) }}
              </span>
            </div>
          </div>
          <Button
            v-if="canUpdate"
            size="small"
            icon="pi pi-plus"
            label="Add"
            :data-testid="`course-reg-add-${registration.id}`"
            @click="openDialog(registration)"
          />
        </div>

        <!-- Stat tiles -->
        <div class="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div class="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
            <p class="text-[11px] uppercase tracking-wide text-gray-500">Total Courses</p>
            <p class="text-lg font-bold text-gray-900 mt-0.5">{{ registration.coursesRegistered?.length ?? 0 }}</p>
          </div>
          <div
            class="border rounded-lg px-3 py-2.5"
            :class="unitsStatusClass(registration)"
          >
            <p class="text-[11px] uppercase tracking-wide opacity-80">Total Units</p>
            <p class="text-lg font-bold mt-0.5">{{ unitsForRegistration(registration) }}</p>
          </div>
          <div class="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
            <p class="text-[11px] uppercase tracking-wide text-gray-500">Min Units</p>
            <p class="text-lg font-bold text-gray-900 mt-0.5">{{ minUnitsLabel }}</p>
          </div>
          <div class="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
            <p class="text-[11px] uppercase tracking-wide text-gray-500">Max Units</p>
            <p class="text-lg font-bold text-gray-900 mt-0.5">{{ maxUnitsLabel }}</p>
          </div>
        </div>
      </div>

      <!-- Courses list -->
      <div class="border-t border-gray-200">
        <div
          v-if="!registration.coursesRegistered?.length"
          class="px-5 py-6 text-center text-sm text-gray-400"
        >
          No courses registered yet.
        </div>
        <template v-else>
          <div class="hidden sm:grid grid-cols-12 gap-3 px-5 py-2 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 font-medium border-b border-gray-200">
            <span class="col-span-3">Code</span>
            <span class="col-span-7">Course Title</span>
            <span class="col-span-1 text-center">Units</span>
            <span class="col-span-1 text-right">Action</span>
          </div>
          <template v-for="(courseReg, index) in registration.moduleGroups.rows" :key="courseReg.id">
            <h4
              v-if="registration.moduleGroups.hasModules && (index === 0 || courseReg.moduleGroupKey !== registration.moduleGroups.rows[index - 1]?.moduleGroupKey)"
              class="px-5 py-3 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-800"
              data-testid="student-registration-module-heading"
            >
              {{ courseReg.module_name || (courseReg.programme_module_id ? 'Module' : 'Courses without a module') }}
            </h4>
            <div
              class="grid grid-cols-12 gap-3 items-center px-5 py-3 border-b border-gray-100 last:border-b-0"
            >
              <span class="col-span-12 sm:col-span-3 font-semibold text-sm text-gray-800">{{ courseReg.course?.course_code || '—' }}</span>
              <span class="col-span-12 sm:col-span-7 text-sm text-gray-600">{{ courseReg.course?.course_title || '—' }}</span>
              <span class="col-span-6 sm:col-span-1 sm:text-center text-sm font-semibold text-gray-700">
                <span class="sm:hidden text-xs text-gray-400 mr-1">Units:</span>
                {{ courseReg.course?.unit ?? '—' }}
              </span>
              <div class="col-span-6 sm:col-span-1 flex justify-end">
                <Button
                  v-if="canDelete"
                  size="small"
                  icon="pi pi-trash"
                  severity="danger"
                  class="p-button-text"
                  :loading="droppingCourses[String(courseReg.course_id)]"
                  :data-testid="`course-reg-drop-${courseReg.id}`"
                  @click="dropCourse(courseReg.course_id, registration.id)"
                />
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>

    <!-- ADD COURSES DIALOG -->
    <Dialog v-model:visible="showDialog" modal header="Select Courses" class="w-full max-w-6xl">
      <div
        v-if="studentAcademicCalendar"
        class="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3"
      >
        <!-- Min -->
        <div class="bg-gray-50 border border-gray-300 rounded-lg p-3 text-center">
          <p class="text-xs text-gray-500">Minimum Units</p>
          <p class="text-lg font-semibold text-gray-800">
            {{ minUnitsAllowed }}
          </p>
        </div>

        <!-- Max -->
        <div class="bg-gray-50 border border-gray-300 rounded-lg p-3 text-center">
          <p class="text-xs text-gray-500">Maximum Units</p>
          <p class="text-lg font-semibold text-gray-800">
            {{ maxUnitsAllowed }}
          </p>
        </div>

        <!-- Selected -->
        <div
          class="rounded-lg p-3 text-center"
          :class="totalUnits < minUnitsAllowed || totalUnits > maxUnitsAllowed
            ? 'bg-red-50 text-red-700'
            : 'bg-primary/10 text-primary'"
        >
          <p class="text-xs">Selected Units</p>
          <p class="text-lg font-bold">
            {{ totalUnits }}
          </p>
        </div>
      </div>

      <div class="flex justify-end mb-3">
        <InputText
          v-model="search"
          placeholder="Search courses..."
          class="w-1/3"
          data-testid="course-reg-search-input"
        />
      </div>
      <DataTable
        size="small"
        :value="curriculum"
        dataKey="course_id"
        responsiveLayout="scroll"
        class="p-datatable-md"
        :loading="loadingSchoolCurriculum"
      >
        <!-- Select Column -->
        <Column header="Select" style="width:4rem">
          <template #body="slotProps">
            <Checkbox
              v-model="selectedCourseIds"
              :value="slotProps.data.course_id"
              :disabled="registeredCourseIds.includes(slotProps.data.course_id) || exceedsMaxUnit(slotProps.data.course_id)"
              :data-testid="`course-reg-select-${slotProps.data.course_id}`"
            />
          </template>
        </Column>

        <!-- Course Code -->
        <Column field="course.course_code" header="Course Code" sortable />
        <!-- Course Title -->
        <Column field="course.course_title" header="Course Title" sortable />
        <Column field="course.course_instance_id" header="Course Instance" sortable>
          <template #body="slotProps">{{ formatCourseInstance(slotProps.data.course) || '—' }}</template>
        </Column>
        <!-- Unit -->
        <Column field="course.unit" header="Unit" style="width:5rem" />
        <!-- Status -->
        <Column header="Status" style="width:8rem">
          <template #body="slotProps">
            <Tag
              :value="slotProps.data.status === 'C' ? 'Compulsory' : 'Elective'"
              :severity="slotProps.data.status === 'C' ? 'success' : 'warning'"
            />
          </template>
        </Column>
      </DataTable>

      <template #footer>
        <Button size="small" label="Cancel" text data-testid="course-reg-cancel-btn" @click="showDialog = false" />
        <Button size="small"
          label="Add Courses"
          icon="pi pi-check"
          :loading="addingCourses"
          data-testid="course-reg-submit-courses-btn"
          @click="submitCourses"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useCurriculum } from '@/composables/queries/useCurriculum';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import { semesterLabel } from '@/utils/semesterLabel';
import { formatCourseInstance } from '@/utils/courseInstance';
import { registrationModuleGroups } from '@/utils/registrationModuleGroups';
import type { StudentCourseRegistration, AddCourseToRegInput, StudentAcademicCalendar } from '@/types/composition-functions';
import { useToast } from 'primevue/usetoast';

const props = defineProps<{
  registrations: StudentCourseRegistration[]
  studentAcademicCalendar?: StudentAcademicCalendar
}>();

const emit = defineEmits<{ refresh: [] }>();

const userStore = useUserStore();
const currentUser = computed(() => userStore.currentUser);
const canUpdate = computed(() =>
  can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.Update, AppPermissions.CourseRegistrationApproval.Resource),
);
const canDelete = computed(() =>
  can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.Delete, AppPermissions.CourseRegistrationApproval.Resource),
);

const localRegistrations = ref<StudentCourseRegistration[]>([...props.registrations]);

// Keep local copy in sync when prop changes
watch(() => props.registrations, (newVal) => {
  localRegistrations.value = [...newVal];
});

/* ---------------- ACADEMIC CALENDAR FILTER ---------------- */
// AcademicCalendarSelect's label is "<session> - <term>" (e.g. "2025/2026 - First Semester")
// and its value is the calendar id. Registrations carry `academic_session` and `semester`
// (number) but no calendar id, so we resolve both pieces from the selected calendar and
// filter on the (session, semester) pair. When no calendar is selected, all registrations
// are shown.
const { academicCalendars } = useSchoolQuery();
const selectedCalendarId = ref<string | number | null>(null);

function termNameToSemester(name?: string | null): number | null {
  if (!name) return null;
  const match = name.match(/(\d+)/);
  if (match) return Number(match[1]);
  const lower = name.toLowerCase();
  if (lower.includes('first')) return 1;
  if (lower.includes('second')) return 2;
  return null;
}

const selectedCalendarMatch = computed(() => {
  if (selectedCalendarId.value === null || selectedCalendarId.value === undefined) return null;
  const cal = (academicCalendars.value ?? []).find(
    (c) => c?.id !== null && c?.id !== undefined && String(c.id) === String(selectedCalendarId.value),
  );
  if (!cal) return null;
  return {
    sessionName: cal.session?.name ?? null,
    semester: termNameToSemester(cal.termStructure?.name),
  };
});

const displayedRegistrations = computed(() => {
  const match = selectedCalendarMatch.value;
  const registrations = !match?.sessionName ? localRegistrations.value : localRegistrations.value.filter((reg) => {
    if (reg.academic_session !== match.sessionName) return false;
    if (match.semester !== null && match.semester !== undefined && reg.semester !== match.semester) return false;
    return true;
  });
  return registrations.map(registration => ({
    ...registration,
    moduleGroups: registrationModuleGroups(registration.coursesRegistered),
  }));
});

/* ---------------- ANALYTICS ---------------- */
function unitsForRegistration(reg: StudentCourseRegistration): number {
  return reg.coursesRegistered.reduce((sum, c) => sum + (c.course?.unit ?? 0), 0);
}

const minUnitsLabel = computed(() => {
  const v = props.studentAcademicCalendar?.min_units_per_registration;
  return v === null || v === undefined ? '—' : v;
});

const maxUnitsLabel = computed(() => {
  const v = props.studentAcademicCalendar?.max_units_per_registration;
  return v === null || v === undefined ? 'No limit' : v;
});

function unitsStatusClass(reg: StudentCourseRegistration): string {
  const total = unitsForRegistration(reg);
  const min = props.studentAcademicCalendar?.min_units_per_registration ?? null;
  const max = props.studentAcademicCalendar?.max_units_per_registration ?? null;
  if ((min !== null && total < min) || (max !== null && total > max)) {
    return 'bg-red-50 border-red-200 text-red-700';
  }
  if (min !== null && max !== null) {
    return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  }
  return 'bg-gray-50 border-gray-200 text-gray-900';
}

/* ---------------- STATE ---------------- */
const showDialog = ref(false);
const toast = useToast();
const activeRegistration = ref<StudentCourseRegistration | null>(null);
const selectedCourseIds = ref<string[]>([]);
const addingCourses = ref(false);
const droppingCourses = ref<Record<string, boolean>>({});

/* ---------------- MUTATIONS ---------------- */
const { addCourseToRegistration, dropCourseFromRegistration } = useTertiaryMutations();
const { curriculum, loadingSchoolCurriculum, search } = useCurriculum({ autoFetch: true });

/* ---------------- HELPERS ---------------- */

const minUnitsAllowed = computed(() => props.studentAcademicCalendar?.min_units_per_registration ?? 0);
const maxUnitsAllowed = computed(() => props.studentAcademicCalendar?.max_units_per_registration ?? Infinity);

const currentUnits = computed(() => {
  if (!activeRegistration.value) return 0;
  return activeRegistration.value.coursesRegistered.reduce((sum, c) => sum + (c.course?.unit ?? 0), 0);
});

const selectedUnits = computed(() => selectedCourseIds.value.reduce((sum, id) => {
  if (activeRegistration.value?.coursesRegistered.some(c => c.course_id === id)) return sum;
  const course = curriculum.value.find((c) => c.course_id === id);
  return sum + (course?.course?.unit ?? 0);
}, 0));

const totalUnits = computed(() => currentUnits.value + selectedUnits.value);

const registeredCourseIds = computed(() =>
  activeRegistration.value?.coursesRegistered.map(c => c.course_id) || [],
);

/* ---------------- OPEN DIALOG ---------------- */
const openDialog = (registration: StudentCourseRegistration) => {
  activeRegistration.value = registration;
  selectedCourseIds.value = registration.coursesRegistered.map(c => c.course_id);
  showDialog.value = true;
};

/* ---------------- EXCEED CHECK ---------------- */
const exceedsMaxUnit = (courseId: string) => {
  const course = curriculum.value.find((c) => c.course_id === courseId);
  if (!course) return false;
  return totalUnits.value + (course.course?.unit ?? 0) > maxUnitsAllowed.value && !registeredCourseIds.value.includes(courseId);
};

/* ---------------- SUBMIT COURSES ---------------- */
const submitCourses = async() => {
  if (!activeRegistration.value) return;

  if (totalUnits.value > maxUnitsAllowed.value || totalUnits.value < minUnitsAllowed.value) {
    toast.add({
      severity: 'error',
      summary: 'Cannot Add Courses',
      detail: 'Units not within allowed range',
      life: 4000,
    });
    return;
  }

  const newlySelectedIds = selectedCourseIds.value.filter(id => !registeredCourseIds.value.includes(id));
  if (!newlySelectedIds.length) {
    showDialog.value = false;
    return;
  }

  const payload: AddCourseToRegInput = {
    course_registration_id: Number(activeRegistration.value.id),
    courses: newlySelectedIds.map(id => {
      const c = curriculum.value.find((c) => c.course_id === id);
      return { course_id: id, status: c?.status ?? 'C' };
    }),
  };

  addingCourses.value = true;

  try {
    await addCourseToRegistration(payload);
    emit('refresh');
  } catch {
    // Toast already surfaced by mutation handler.
  } finally {
    addingCourses.value = false;
    showDialog.value = false;
  }
};

const dropCourse = async(courseId: number | string, registrationId: number | string) => {
  const key = String(courseId);
  droppingCourses.value[key] = true;

  try {
    await dropCourseFromRegistration({
      course_registration_id: Number(registrationId),
      course_ids: [Number(courseId)],
    });
    emit('refresh');
  } catch {
    // Toast already surfaced by mutation handler.
  } finally {
    droppingCourses.value[key] = false;
  }
};

/* ---------------- WATCH TOTAL UNITS ---------------- */
watch(totalUnits, (val) => {
  if (!showDialog.value) return;
  if (val > maxUnitsAllowed.value) {
    toast.add({
      severity: 'warn',
      summary: 'Unit Limit Exceeded',
      detail: `Maximum allowed units is ${maxUnitsAllowed.value}`,
      life: 3000,
    });
  }
  if (val < minUnitsAllowed.value) {
    toast.add({
      severity: 'info',
      summary: 'Below Minimum Units',
      detail: `Minimum required units is ${minUnitsAllowed.value}`,
      life: 3000,
    });
  }
});
</script>
